import { NavigationService } from '@tempots/ui'

export interface UnsavedChangesGuardOptions {
  /** Re-checked on every navigation attempt. */
  isDirty: () => boolean
  /**
   * Called instead of navigating when there are unsaved changes. Invoke
   * `proceed` to perform the blocked navigation after all (it bypasses the
   * guard for that one action).
   */
  onBlocked: (proceed: () => void) => void
}

/**
 * Blocks ways of leaving the page while there are unsaved changes:
 *
 * 1. In-app anchor clicks (top-bar nav, logo) — a capture-phase document
 *    listener runs before the router's own click handler. Anchors navigate
 *    through their Location handle, NOT NavigationService, so wrapping the
 *    service alone would miss them.
 * 2. Programmatic NavigationService.navigate calls (hamburger/user menus).
 *    `replace` is deliberately left unwrapped: the app only uses it for
 *    auth redirects, which must not be blocked by a dialog.
 * 3. Tab close / reload / external navigation, via the browser's native
 *    beforeunload prompt (custom UI is not possible there).
 *
 * Known gap: the browser back/forward buttons (popstate). The router
 * processes popstate before any app code could ask for confirmation, so
 * those are not guarded.
 *
 * Returns a dispose function that uninstalls everything.
 */
export function installUnsavedChangesGuard({
  isDirty,
  onBlocked,
}: UnsavedChangesGuardOptions): () => void {
  let bypass = false
  const shouldBlock = () => !bypass && isDirty()
  const runBypassed = (action: () => void) => {
    bypass = true
    try {
      action()
    } finally {
      bypass = false
    }
  }
  const block = (action: () => void) => onBlocked(() => runBypassed(action))

  const onClickCapture = (e: MouseEvent) => {
    if (!shouldBlock()) return
    if (e.defaultPrevented || e.button !== 0) return
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    const anchor = (e.target as HTMLElement | null)?.closest?.(
      'a[href]'
    ) as HTMLAnchorElement | null
    if (!anchor) return
    if (anchor.target && anchor.target !== '_self') return
    // External links trigger a real unload — beforeunload covers those.
    if (anchor.origin !== window.location.origin) return
    const href = anchor.getAttribute('href')
    if (!href) return
    // Fragment-only hrefs are in-page actions (e.g. the tour's skip link),
    // not navigation — blocking them pops the leave dialog under the tour.
    if (href.startsWith('#')) return
    e.preventDefault()
    e.stopPropagation()
    block(() => NavigationService.navigate(href))
  }
  document.addEventListener('click', onClickCapture, true)

  const rawNavigate = NavigationService.navigate
  NavigationService.navigate = function (url, options) {
    if (!shouldBlock()) return rawNavigate.call(NavigationService, url, options)
    block(() => rawNavigate.call(NavigationService, url, options))
  }

  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (!shouldBlock()) return
    e.preventDefault()
    // Required by some browsers to actually show the prompt.
    e.returnValue = ''
  }
  window.addEventListener('beforeunload', onBeforeUnload)

  return () => {
    document.removeEventListener('click', onClickCapture, true)
    NavigationService.navigate = rawNavigate
    window.removeEventListener('beforeunload', onBeforeUnload)
  }
}
